// ! Utility to help link sources

import config from '../../cfg';
import store from '../../redux/store';

import * as Selectors from '../../redux/selectors';
import * as NetworkActions from '../../redux/actions/network.actions';
import * as UIAction from '../../redux/actions/ui.actions';
import * as LinkHelper from '../../utils/LinkHelper';

import { Buffer } from 'buffer';
import { poll } from '@hotglue/common';
import { Alert } from '../popupAlerts';
import {
  disconnectLinkedConnector,
  initializeOauth1Flow,
  isEntityLinked,
  updateLinkedConnectorConfig,
  createOauthStateToken,
} from '../../api/client';

export const handleDirectLinkConnector = async (connector, values = {}) => {
  const { type, id, label, tenant_id, connection_name } = connector;

  const updatedConnector = Object.assign({}, connector, values);

  const state = store.getState();

  const isConnected = state.connectors.selectedConnector?.id === id;
  if (type === 'oauth' && connector['connect_ui_params']) {
    const params = connector['connect_ui_params'];
    // Check if there are any flow specific params
    const flow_params = Object.entries(params).filter(([field, value]) => {
      let { flow_id } = value;

      return (
        flow_id &&
        flow_id === Selectors.getSelectedFlow() &&
        (!values.config || (values.config && values.config[field] === undefined))
      );
    });

    if (flow_params.length > 0) {
      // Open AppParams
      UIAction.showConnectParams(updatedConnector);
      return;
    }
  }

  // If the connector is an oauth connector, update the linked connector config in s3
  const response = await store.dispatch(NetworkActions.doAddLinkedConnector(updatedConnector));
  // Check if request was successful
  if (response?.status === 'error') {
    throw response.payload;
  }

  if (type === 'oauth' || (type === 'oauth1' && !updatedConnector.symlink && !isConnected)) {
    const auth_url =
      type === 'oauth1'
        ? await initializeOauth1Flow(id, values, undefined, tenant_id)
        : await getAuthUrl(id, values, undefined, tenant_id, connection_name);
    if (!auth_url) return;
    createAndPollLink(auth_url, label, id, tenant_id);

    // Don't trigger listener until OAuth is linked
    return response;
  }

  await store.dispatch(NetworkActions.fetchLinkedConnectors());

  // Alert listener if exists
  const listener = Selectors.getListener();
  if (listener && listener.onConnectorLinked) {
    listener.onConnectorLinked(connector, Selectors.getSelectedFlow(), Selectors.getTenantId());
  }

  return await response;
};

export const getAuthUrl = async (
  connectorId,
  values = {},
  flow = Selectors.getSelectedFlow(),
  user_id = Selectors.getTenantId(),
  connection_name = undefined,
) => {

  const connector = Selectors.selectSupportedConnectorById(connectorId);

  if (!connector) return undefined;

  let base_url = connector.auth_url;

  if (values && values.config) {
    // Handle variables in base_url (used for Shopify)
    Object.entries(values.config).forEach(([k, v]) => {
      base_url = base_url.replace(`{${k}}`, v);
    });
  }

  const env_id = Selectors.getEnvId();
  const api_key = Selectors.getApiKey();
  const callbackDomain = Selectors.getCallbackDomain();
  const callbackDomainName = Selectors.getCallbackDomainName();
  const callbackFavicon = Selectors.getCallbackFavicon();
  const redirectUri = Selectors.getRedirectUri();
  const hideText = Selectors.getHideText();
  const bgColor = Selectors.getBgColor();
  const skipCallbackOAuthErrors = Selectors.skipCallbackOAuthErrors();
  const api_url = config.api_url;
  let callbackUrl = redirectUri || config.redirect_url;

  // Build the state needed by the callback that completes OAuth flow
  const state = {
    user_id,
    env_id,
    flow,
    flowVersion: 2,
    entity_id: connector.id,
    entity_type: 'connector',
    api_url: `${api_url}/v2/entity/auth`,
    api_key,
    domain: callbackDomain || 'hotglue.xyz',
    domainName: callbackDomainName,
    favicon: callbackFavicon,
    connection_name,
    hideText,
    bgColor,
    skipCallbackOAuthErrors,
    popupShouldClose: true,
  };

  const stateTokenResponse = await createOauthStateToken(state);
  const stateToken = stateTokenResponse.token;

  if (connectorId === 'snowflake:oauth') {
    callbackUrl = encodeURI(callbackUrl);
  }
  let url = `${base_url}&state=HGTOKEN-${stateToken}&redirect_uri=${callbackUrl}`;
  if (connectorId === 'woocommerce:oauth') {
    url = url.replace('&state=', '&user_id=').replace('&redirect_uri=', '&return_url=');
    url += `&callback_url=${api_url}/v2/entity/auth`;
  }

  if (connectorId === 'faire:oauth') {
    url = new URL(url);
    url.searchParams.set('applicationId', url.searchParams.get('client_id'));
    url.searchParams.delete('client_id');
    url.searchParams.set('redirectUrl', url.searchParams.get('redirect_uri'));
    url.searchParams.delete('redirect_uri');
    url = url.toString();
  }

  return url;
};

// Polls to see if the connector is linked
export const pollLink = (
  connectorId,
  label,
  pollWindowClosed,
  tenantId = undefined,
  nextStep = store.getState().configuration.nextStep,
  isReconnect = undefined,
) => {
  /**
   * When the connector has "connect_ui_params", we first create a "config.json"
   * with the "connect_ui_params" data in it. But the flow is not linked yet.
   *
   * So we're adding a "is flow linked" check in order to determine if the
   * oauth linking is finished or not
   */
  const selectedFlowId = Selectors.getSelectedFlow();

  let retryCount = 0;
  
  // approximately 5 minutes of polling
  const maxRetryCount = 300;

  /**
   * We encountered a few cases where the "poll window" appears to close
   * right after it opens.
   *
   * To make the link work, we had disabled the "pollWindowClosed()" check.
   * That works but it created another "weird behavior" that if the user manually
   * close the "poll window", the poll continues until it reach the "maxRetryCount".
   *
   * To make it better, I'm (davi) creating this "shouldCheckWindowStatus" variable
   * that is going to control whether we make the "pollWindowClosed()" check or not.
   *
   * 1. If the "poll window" is closed before the poll starts, we should not check the
   *    "pollWindowClosed()" because we encountered the aforementioned error. Therefore,
   *    "shouldCheckWindowStatus" is going to be "false"
   * 2. If the "poll window" closes before the first 2 "request" function calls, we are
   *    going to consider that we encountered the same error. Therefore, "shouldCheckWindowStatus"
   *    is going to be "false"
   *
   * Also we're letting the poll do one more request in case the "poll window" closes. For that,
   * we're creating this "shouldCancelOnWindowClose" variable. When we first detect that the
   * "poll window" is closed, we just set this new variable to "true". So in the next iteration
   * we can actually stop the polling and check if the link was successful or not.
   * We had to do this because we've encountered a possible "race conditon" in which the link
   * was successfull but the widget showed that the link was canceled
   */
  let shouldCheckWindowStatus = !pollWindowClosed();
  let shouldCancelOnWindowClose = false;

  const { future, cancel } = poll({
    request: () => {
      // Don't call cancel() directly here, as it breaks the Promise chain
      // Instead, return a rejected Promise if needed
      if (!Selectors.getSelectedFlow()) {
        console.log('No selected flow, stopping poll');
        // Schedule the cancel for after this request completes
        setTimeout(() => cancel(), 0);
        // Return a resolved Promise with empty data to keep the chain intact
        return Promise.resolve([{ payload: [] }, { payload: [] }]);
      }

      if (shouldCheckWindowStatus) {
        if (pollWindowClosed() && retryCount < 2) {
          shouldCheckWindowStatus = false;
        } else if (pollWindowClosed()) {
          if (shouldCancelOnWindowClose) {
            // Schedule the cancel for after this request completes
            setTimeout(() => cancel(), 0);
          } else {
            shouldCancelOnWindowClose = true;
          }
        }
      }
      retryCount++;
      return Promise.all([
        store.dispatch(NetworkActions.fetchLinkedConnector(connectorId)),
        store.dispatch(NetworkActions.fetchLinkedFlows(tenantId)),
      ]);
    },
    pollingPeriod: 1000,
    shouldStop: (r) => {
      if (!Selectors.getSelectedFlow()) return true;

      const [{ payload: response }, { payload: linkedFlows }] = r;

      const isFlowLinked = Boolean(Array.isArray(linkedFlows) && linkedFlows.find(({ id }) => id === selectedFlowId));

      if (shouldCheckWindowStatus && retryCount >= 2 && pollWindowClosed()) {
        if (shouldCancelOnWindowClose) {
          return true;
        }
        shouldCancelOnWindowClose = true;
        return false;
      }

      if (Array.isArray(response) && response.length == 1) {
        const connectorConfig = tenantId ? response.find(({ tenant_id }) => tenant_id === tenantId) : response[0];

        if (isFlowLinked && connectorConfig && !connectorConfig.disconnected && !connectorConfig.isReconnecting)
          return true;

        return retryCount > maxRetryCount;
      }

      return retryCount > maxRetryCount;
    },
  });

  future
    .then((response) => {
      const [{ payload: linkedConnectors }, { payload: linkedFlows }] = response;

      const isFlowLinked = Boolean(Array.isArray(linkedFlows) && linkedFlows.find(({ id }) => id === selectedFlowId));

      const tenantLinkedConnector =
        linkedConnectors.find(({ tenant_id }) => tenant_id === tenantId) ?? linkedConnectors[0];

      const listener = Selectors.getListener();

      const error = tenantLinkedConnector?.error;

      if (error) {
        Alert({ type: 'error', text: `An error occurred while linking ${label}. ${error && error.message}` });
        if (error?.message && listener?.onConnectorLinkFailed) {
          listener.onConnectorLinkFailed(
            connectorId,
            selectedFlowId,
            tenantId ?? Selectors.getTenantId(),
            error.messsage,
          );
        }
        return LinkHelper.postLink(undefined, 'connector', Selectors.getSelectedFlow()).finally(() => {
          UIAction.stopSourceLink();
        });
      }

      if (
        retryCount > maxRetryCount ||
        !isFlowLinked ||
        !linkedConnectors ||
        !(Array.isArray(linkedConnectors) && linkedConnectors.length > 0)
      ) {
        Alert({ type: 'error', text: `Linking ${label} cancelled.` });
        // Alert listener if exists
        //TODO: Create a new Listener
        if (listener && listener.onConnectorLinkCanceled) {
          listener.onConnectorLinkCanceled(connectorId, Selectors.getSelectedFlow());
        }

        const isEntityLinked =
          isFlowLinked && (linkedFlows.find(({ id }) => id === selectedFlowId)?.connectors ?? []).includes(connectorId);

        let promise = null;

        // If the poll was canceled and the "isEntityLinked" is false, we need to call "disconnectLinkedConnector" to delete files in S3.
        // We need to do this because sometimes we would show that a tenant exists although this tenant was never linked
        if (!isEntityLinked) {
          promise = disconnectLinkedConnector(connectorId, selectedFlowId, tenantId);
        }

        const onFinally = () => UIAction.stopSourceLink();

        if (promise !== null) {
          promise.finally(() => {
            LinkHelper.postLink(undefined, 'source', Selectors.getSelectedFlow()).finally(onFinally);
          });
        } else {
          onFinally();
        }

        return;
      }

      LinkHelper.postLink(undefined, 'connector', Selectors.getSelectedFlow()).then(() => {
        UIAction.stopSourceLink();
        Alert({ type: 'success', text: `${label} linked!` });

        if (nextStep === 'fieldMap') {
          UIAction.setOpenWidget();
          UIAction.goNextStep({ ...tenantLinkedConnector, entityType: 'connector' }, nextStep);
        } else if (
          nextStep === 'goToConfig' ||
          nextStep === 'mapping' ||
          nextStep === 'credentials' ||
          nextStep === 'triggers' || 
          nextStep === 'selectFile'
        ) {
          UIAction.setOpenWidget();
          UIAction.goToEntityConfig(
            tenantLinkedConnector,
            'connector',
            selectedFlowId,
            nextStep === 'credentials',
            nextStep === 'triggers',
            nextStep === 'selectFile'
          );
        } else if (['google-sheets', 'google-drive'].includes(tenantLinkedConnector.id)) {
          store.getState().ui.isConnection
            ? UIAction.openEntityConfig(tenantLinkedConnector, 'connector', selectedFlowId)
            : UIAction.goToEntityConfig(tenantLinkedConnector, 'connector', selectedFlowId);
        }
        UIAction.setIsLinking(false);
        isReconnect && UIAction.selectEntity(tenantLinkedConnector, 'connector');

        // Alert listener if exists
        const listener = Selectors.getListener();
        if (
          listener &&
          listener.onConnectorLinked &&
          !tenantLinkedConnector.disconnected &&
          !tenantLinkedConnector.isReconnecting
        ) {
          const linkedConnector = tenantLinkedConnector;
          if (isReconnect !== undefined) {
            linkedConnector.reconnected = isReconnect;
          }
          listener.onConnectorLinked(linkedConnector, Selectors.getSelectedFlow(), Selectors.getTenantId());
        }
      });
    })
    .catch((e) => {
      console.error(e);
      Alert({ type: 'error', text: `An error occurred while linking ${label}. ${e}` });
      UIAction.stopSourceLink();
    });
};

export function createAndPollLink(
  auth_url,
  label,
  connectorId,
  tenantId = undefined,
  nextStep,
  isReconnect = undefined,
) {
  let windowStatus = { closed: false };

  const onClose = () => {
    windowStatus.closed = true;

    // Alert listener if exists
    const listener = Selectors.getListener();
    if (listener && listener.onPopupClose) {
      listener.onPopupClose(connectorId, Selectors.getSelectedFlow());
    }
  };
  const pollWindowClosed = () => windowStatus.closed;
  LinkHelper.createWindowPopup(auth_url, `Link your ${label} data`, 800, 600, onClose, 'id');
  pollLink(connectorId, label, pollWindowClosed, tenantId, nextStep, isReconnect);
}

export const doLink = async (connector, tenantId = undefined, nextStep, connectValues, checkLink = true) => {
  const { id, type, label, connect_ui_params, connection_name, options } = connector;
  const autoLink = options?.auto_link ?? false;

  if (id === 'file') {
    try {
      // Link the file source
      await handleDirectLinkConnector(connector);
      // Refetch the flows
      await LinkHelper.postLink();
      // Open file source
      UIAction.goToEntityConfig(connector);
    } catch (err) {
      console.error(err);
    }
  } else if (connectValues || autoLink) {
    await handleDirectLinkConnector(
      { ...connector, tenant_id: tenantId ?? connector.tenant_id },
      connectValues && { config: connectValues },
    );
  } else if (connect_ui_params) {
    UIAction.showConnectParams({ ...connector, tenant_id: tenantId }, 'connector', nextStep);
  } else if (type === 'oauth') {
    const auth_url = await getAuthUrl(id, connector, undefined, tenantId, connection_name);
    if (!auth_url) return;

    if (!checkLink) {
      try {
        await updateLinkedConnectorConfig(connector.id, { isReconnecting: true }, undefined, tenantId);
      } catch (err) {
        console.log(err)
      }
    }

    createAndPollLink(auth_url, label, id, tenantId, nextStep);
  }
};

export const pollJob = async (jobRoot, flow, userId, entityId) => {
  const { jobs } = store.getState();
  const jobState = jobs.polling[`${flow}/${userId}`];

  let isPolling = undefined;
  if (jobState) isPolling = jobState[jobRoot];

  if (isPolling !== undefined && isPolling) return {};
  else UIAction.setAPollingJob(jobRoot, flow, userId, true);

  const { future } = poll({
    request: () => store.dispatch(NetworkActions.fetchJobStatus(jobRoot, flow, userId, entityId)),
    pollingPeriod: 15000,
    shouldStop: ({ payload: job }) => {
      console.log(`Polling job ${jobRoot} - status=${job && job.status}`);
      return !job || job.status.includes('FAILED') || job.status.includes('COMPLETE');
    },
  });

  future.then(({ payload: job }) => {
    if (job.status.includes('FAILED')) Alert({ type: 'error', text: `Job completed with errors.` });
    else Alert({ type: 'success', text: 'Job completed successfully' });
    UIAction.setAPollingJob(jobRoot, flow, userId, false);
  });

  return future;
};

export const preLink = async ({
  hasLinkedConnector,
  demo,
  connector,
  flow,
  shouldSkipCopy = false,
  tenantId = undefined,
  setLoading,
}) => {
  if (hasLinkedConnector) {
    return Alert({
      text: 'Disconnect linked connector before switching to another',
      type: 'error',
    });
  }

  if (demo) {
    return Alert({
      type: 'info',
      text: `Sorry, you can't connect connectors in demo mode!`,
    });
  }

  setLoading(true);

  const { id } = connector;

  // Check if source is already linked in another flow
  const oFlows = await isEntityLinked(id);

  const filteredFlows = Array.isArray(oFlows) && oFlows.filter((f) => f.id !== flow);

  if (!shouldSkipCopy && filteredFlows?.length > 0) {
    setLoading(false);
    // Ask if they want to copy tap config from one, nextStep of these
    UIAction.showCopyEntity(connector, oFlows);
  } else {
    await doLink(connector, tenantId);

    setLoading(false);
  }
};
